import { LBtn, LCheckbox, LFlex, LTableResults, LTableSearch } from "@components";
import { DropdownMenuOption, LDropdownMenu } from "@components/LDropdownMenu";
import { LTooltip } from "@components/LTooltip";
import { NavItems } from "@helpers/navigation";
import { apiOrganizationStatus, getReinstateRevokeToolTipText, OrganizationStatus } from "@helpers/organizationHelper";
import { useApi } from "@services/useApi";
import { Organization } from "@store/generalTypes";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import _ from "lodash";
import { ChangeEvent, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { NavLink, useNavigate } from "react-router-dom";

export const SuperOrganizations = (): JSX.Element => {
    const validateMethods = useForm();
    const { getOrganizations, updateOrganizationStatus, deleteOrganization, loading } = useApi();
    const [data, setData] = useState<Organization[]>([] as Organization[]);
    const [originalData, setOriginalData] = useState<Organization[]>();
    const navigate = useNavigate();

    const handleGetOrganizations = async (): Promise<void> => {
        const orgs = await getOrganizations();
        setData(orgs.items);
        setOriginalData(orgs.items);
    };

    useEffect(() => {
        void handleGetOrganizations();
    }, []);

    const columnHelper = createColumnHelper<Organization>();
    const baseColumns: Array<ColumnDef<Organization, any>> = [
        {
            id: "select",
            header: ({ table }) => (
                <LCheckbox
                    classes="pl-1 pt-1"
                    inputName="checkAllOptions"
                    checked={table.getIsAllRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                />
            ),
            cell: ({ row }) => (
                <LCheckbox
                    classes="pl-1 pt-1"
                    value={row.original.id.toString()}
                    inputName={"row-" + row.original.id.toString()}
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                />
            )
        },
        columnHelper.accessor("name", {
            header: () => "Account",
            cell: ({ row }) => (
                <NavLink to={NavItems.SuperAdmin.OrganizationDetails.path + row.original.id.toString()}>
                    {row.original.name}
                </NavLink>
            )
        }),
        columnHelper.accessor("id", {
            header: () => "ID",
            cell: ({ row }) => (
                <NavLink to={NavItems.SuperAdmin.OrganizationDetails.path + row.original.id.toString()}>
                    {row.original.id}
                </NavLink>
            )
        }),
        columnHelper.accessor("contactLastName", {
            header: () => "Main Contact",
            cell: ({ row }) => (
                <NavLink to={NavItems.SuperAdmin.OrganizationDetails.path + row.original.id.toString()}>
                    {`${row.original.contactFirstName} ${row.original.contactLastName}`}
                </NavLink>
            )
        }),
        {
            id: "assessments",
            header: () => "Asessments",
            cell: ({ row }) => (
                <NavLink to={NavItems.SuperAdmin.OrganizationDetails.path + row.original.id.toString()}>
                    {`${row.original.usedAssessments} / ${row.original.totalAssessments}`}
                </NavLink>
            )
        },
        {
            id: "utilization",
            header: () => "Utilization (%)",
            cell: ({ row }) => {
                const val = row.original;
                const calc = calcPercentage(val.usedAssessments, val.totalAssessments);
                return (
                    <NavLink to={NavItems.SuperAdmin.OrganizationDetails.path + row.original.id.toString()}>
                        {!Number.isNaN(calc) ? `${calc.toFixed(0)}%` : "N/A"}
                    </NavLink>
                );
            }
        },
        columnHelper.accessor("status", {
            header: () => "Status",
            cell: ({ row }) => {
                const className = [
                    apiOrganizationStatus[OrganizationStatus.accessRevoked],
                    apiOrganizationStatus[OrganizationStatus.purchaseIncomplete]
                ].includes(row.original.status as any)
                    ? "has-text-danger"
                    : "";
                return (
                    <NavLink
                        className={className}
                        to={NavItems.SuperAdmin.OrganizationDetails.path + row.original.id.toString()}
                    >
                        {_.startCase(row.original.status)}
                    </NavLink>
                );
            }
        }),
        {
            id: "menu",
            enableSorting: false,
            cell: (cell) => {
                return (
                    <LDropdownMenu
                        id={`menu_${cell.row.original.id}`}
                        options={getOptions(cell.row.original)}
                        isRightAligned
                        type="tableRowMenu"
                        hideErrorLabel
                        theme="light"
                    />
                );
            }
        }
    ];

    const getOptions = (organization: Organization): DropdownMenuOption[] => {
        const isAccessRevoked =
            (organization.status as string) === apiOrganizationStatus[OrganizationStatus.accessRevoked];
        const optionVerb = isAccessRevoked ? "Reinstate" : "Revoke";

        return [
            {
                optionName: `${optionVerb} Access`,
                disabled: loading,
                children: (
                    <div className="columns m-2 is-align-items-center is-justify-content-space-between">
                        <LBtn
                            classes="has-text-danger has-text-weight-bold"
                            variation="unstyled"
                            noSpacing
                            loading={loading}
                            onClick={async () => {
                                await handleUpdateOrganizationStatus(
                                    organization.id,
                                    isAccessRevoked ? OrganizationStatus.active : OrganizationStatus.accessRevoked
                                );
                            }}
                        >
                            {`${optionVerb} Access`}
                        </LBtn>
                        <LTooltip text={getReinstateRevokeToolTipText(organization.status)} />
                    </div>
                )
            },
            {
                optionName: "Delete Organization",
                disabled: loading,
                children: (
                    <div className="columns m-2 is-align-items-center is-justify-content-space-between">
                        <LBtn
                            classes="has-text-danger has-text-weight-bold"
                            variation="unstyled"
                            noSpacing
                            loading={loading}
                            onClick={async () => {
                                await handleDeleteOrganization(organization.id);
                            }}
                        >
                            Delete Organization
                        </LBtn>
                        <LTooltip text="By deleting the organization you are permanently removing them from the database. This action cannot be undone." />
                    </div>
                )
            }
        ];
    };

    const searchOrganizations = (searchText: string, searchData: Organization[]): Organization[] => {
        return searchData.filter(
            (o) =>
                o.contactFirstName.toLowerCase().includes(searchText.toLowerCase()) ||
                o.contactLastName.toLowerCase().includes(searchText.toLowerCase()) ||
                o.id.toString().includes(searchText.toLowerCase()) ||
                o.name.toLowerCase().includes(searchText.toLowerCase()) ||
                o.status.toLowerCase().includes(searchText.toLowerCase())
        );
    };

    const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
        if (originalData) setData(searchOrganizations(e.target.value, originalData));
    };

    const handleOnNewOrgClick = (): void => {
        navigate(NavItems.SuperAdmin.CreateOrganization.path);
    };

    const handleDeleteOrganization = async (organizationId: number): Promise<void> => {
        try {
            await deleteOrganization(organizationId);
        } finally {
            await handleGetOrganizations();
        }
    };

    const handleUpdateOrganizationStatus = async (
        organizationId: number,
        status: OrganizationStatus.active | OrganizationStatus.accessRevoked
    ): Promise<void> => {
        try {
            await updateOrganizationStatus(organizationId, status);
        } finally {
            await handleGetOrganizations();
        }
    };

    return (
        <FormProvider {...validateMethods}>
            <LFlex row alignCenter className="is-justify-content-space-between">
                <div style={{ width: "100%" }}>
                    <LTableSearch
                        numRecords={data.length}
                        label="Search Organizations"
                        showInactive={true}
                        inactiveCount={
                            data.filter((o) => o.status.toLowerCase() === OrganizationStatus.inactive.toLowerCase())
                                .length ?? 0
                        }
                        resultsLabel="Organizations"
                        resultCountContainerSize="7"
                        handleSearch={handleSearch}
                    />
                </div>

                <LBtn noSpacing type="button" isBold onClick={handleOnNewOrgClick} classes="mb-4">
                    + Create New Organization
                </LBtn>
            </LFlex>
            {LTableResults<Organization>(data, baseColumns, { isRoundedHeader: true })}
        </FormProvider>
    );
};

const calcPercentage = (used: number, total: number): number => {
    return (100 * used) / total;
};
